import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import type { TableActionItem } from '@/components/shared';
import { fetchBackofficeOrganizationSalesHistory } from '../api';
import type { BackofficeOrganizationSalesHistoryEntry, OrganizationSalesFilter } from '../types';
import { OrganizationSalesFilters } from './OrganizationSalesFilters';
import { OrganizationSalesTable } from './OrganizationSalesTable';

const DEFAULT_LIMIT = 20;
const SALES_FILTER_VALUES = new Set<OrganizationSalesFilter>([
  'all',
  'completed',
  'partially_refunded',
  'refunded',
  'canceled',
  'pending',
]);

function readSalesFilter(value: string | null): OrganizationSalesFilter {
  return value && SALES_FILTER_VALUES.has(value as OrganizationSalesFilter)
    ? (value as OrganizationSalesFilter)
    : 'all';
}

export interface OrganizationSalesListSectionProps {
  organizationId: string;
  organizationName: string;
  surface?: 'page' | 'tile' | undefined;
}

export function OrganizationSalesListSection({
  organizationId,
  organizationName,
  surface = 'page',
}: OrganizationSalesListSectionProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [entries, setEntries] = useState<BackofficeOrganizationSalesHistoryEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const loadingMoreRef = useRef(false);
  const refreshAbortControllerRef = useRef<AbortController | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const filter = readSalesFilter(searchParams.get('salesFilter'));
  const requestParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set('filter', filter);
    return params;
  }, [filter]);
  const hasMore = entries.length < total;

  const loadEntries = useCallback(
    async (offset: number, signal: AbortSignal) => {
      const params = new URLSearchParams(requestParams);
      params.set('limit', String(DEFAULT_LIMIT));
      params.set('offset', String(offset));

      const response = await fetchBackofficeOrganizationSalesHistory(
        organizationId,
        params,
        signal
      );
      setTotal(response.page.total);
      setEntries((current) =>
        offset === 0 ? response.entries : [...current, ...response.entries]
      );
    },
    [organizationId, requestParams]
  );

  const handleRefresh = useCallback(async () => {
    refreshAbortControllerRef.current?.abort();
    const controller = new AbortController();
    refreshAbortControllerRef.current = controller;

    setRefreshing(true);
    setError(null);
    setLoadMoreError(null);

    try {
      await loadEntries(0, controller.signal);
    } catch (caught: unknown) {
      if (caught instanceof DOMException && caught.name === 'AbortError') {
        return;
      }
      setEntries([]);
      setTotal(0);
      setError(caught instanceof Error ? caught.message : 'Failed to refresh sales history');
    } finally {
      if (refreshAbortControllerRef.current === controller) {
        refreshAbortControllerRef.current = null;
      }
      if (!controller.signal.aborted) {
        setRefreshing(false);
      }
    }
  }, [loadEntries]);

  useEffect(() => {
    const controller = new AbortController();
    setInitialLoading(true);
    setError(null);
    setLoadMoreError(null);

    loadEntries(0, controller.signal)
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') {
          return;
        }
        setEntries([]);
        setTotal(0);
        setError(caught instanceof Error ? caught.message : 'Failed to load sales history');
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setInitialLoading(false);
        }
      });

    return () => controller.abort();
  }, [loadEntries]);

  useEffect(() => {
    return () => refreshAbortControllerRef.current?.abort();
  }, []);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || initialLoading || !hasMore) {
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }

    const controller = new AbortController();
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || loadingMoreRef.current) {
          return;
        }

        observer.disconnect();
        loadingMoreRef.current = true;
        setLoadingMore(true);
        setLoadMoreError(null);
        loadEntries(entries.length, controller.signal)
          .catch((caught: unknown) => {
            if (caught instanceof DOMException && caught.name === 'AbortError') {
              return;
            }
            setLoadMoreError(
              caught instanceof Error ? caught.message : 'Failed to load more sales history'
            );
          })
          .finally(() => {
            loadingMoreRef.current = false;
            if (!controller.signal.aborted) {
              setLoadingMore(false);
            }
          });
      },
      { rootMargin: '280px' }
    );

    observer.observe(target);

    return () => {
      controller.abort();
      observer.disconnect();
    };
  }, [entries.length, hasMore, initialLoading, loadEntries]);

  const tableActions = useMemo<TableActionItem[]>(
    () => [
      {
        id: 'refresh',
        label: 'Refresh',
        loadingLabel: 'Refreshing',
        icon: RefreshCw,
        onSelect: () => {
          void handleRefresh();
        },
        placement: 'fixed-inline',
        disabled: initialLoading,
        isLoading: refreshing,
      },
    ],
    [handleRefresh, initialLoading, refreshing]
  );

  function updateFilter(nextFilter: OrganizationSalesFilter) {
    const next = new URLSearchParams(searchParams);
    if (nextFilter === 'all') {
      next.delete('salesFilter');
    } else {
      next.set('salesFilter', nextFilter);
    }
    next.delete('offset');
    setSearchParams(next, { replace: true });
  }

  if (initialLoading) {
    return (
      <section
        className={
          surface === 'tile'
            ? 'h-full bg-white'
            : 'rounded-lg border border-neutral-200 bg-white shadow-sm'
        }
      >
        <div className="px-5 py-16 text-center text-body text-neutral-500">
          Loading sales history
        </div>
      </section>
    );
  }

  return (
    <section
      className={
        surface === 'tile'
          ? 'flex h-full min-h-full flex-col overflow-hidden bg-white'
          : 'rounded-lg border border-neutral-200 bg-white shadow-sm'
      }
    >
      <OrganizationSalesFilters
        filter={filter}
        actions={tableActions}
        onFilterChange={updateFilter}
      />

      {error ? (
        <div className="px-5 py-16 text-center text-body text-red-700">{error}</div>
      ) : entries.length === 0 ? (
        <div className="px-5 py-16 text-center text-body text-neutral-500">
          No sales history found for this organization.
        </div>
      ) : (
        <div className={surface === 'tile' ? 'min-h-0 flex-1 overflow-y-auto' : undefined}>
          <OrganizationSalesTable organizationName={organizationName} entries={entries} />
          <div ref={loadMoreRef} className="px-5 py-4 text-small text-neutral-500">
            {loadingMore
              ? 'Loading more sales history'
              : hasMore
                ? 'Scroll to load more sales history'
                : `Showing all ${total} sales history entries`}
          </div>
          {loadMoreError ? (
            <div className="px-5 pb-4 text-small text-red-700">{loadMoreError}</div>
          ) : null}
        </div>
      )}
    </section>
  );
}
